/**
 * Intrlock Panel kiosk mode helpers.
 *
 * The visitor app runs in two contexts:
 *   1. Normal browser (visitor.intrlock.io) — touch / mouse / has its own OSK
 *   2. Intrlock Panel — a Pi-based hardware kiosk. The panel server runs on
 *      http://127.0.0.1:8080 and exposes endpoints that show/hide a
 *      Wayland-native on-screen keyboard (wvkbd). Chromium has no native
 *      OSK plumbing on labwc, so we drive show/hide from JavaScript by
 *      listening for input focus.
 *
 * The kiosk launcher appends `?kiosk=1` to the visitor URL when serving the
 * panel, so detection is free — we just check the query string once per
 * page load and persist into sessionStorage for navigation between pages.
 */

const STORAGE_KEY = 'intrlock_kiosk_mode';
const PANEL_BASE = 'http://127.0.0.1:8080';

let _isKiosk: boolean | null = null;

/** Detect kiosk mode from `?kiosk=1` once and cache for the session. */
export function isKioskMode(): boolean {
    if (_isKiosk !== null) return _isKiosk;
    try {
        if (typeof window === 'undefined') return (_isKiosk = false);
        const url = new URL(window.location.href);
        if (url.searchParams.get('kiosk') === '1') {
            sessionStorage.setItem(STORAGE_KEY, '1');
        }
        _isKiosk = sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch {
        _isKiosk = false;
    }
    return _isKiosk;
}

/** Fire-and-forget POST to the panel server. */
async function panelPost(path: string): Promise<void> {
    // Defense-in-depth: never reach for 127.0.0.1 outside an actual
    // panel session. The caller path already gates via
    // installKioskKeyboardListeners, but a future caller might forget.
    if (!isKioskMode()) return;
    try {
        await fetch(`${PANEL_BASE}${path}`, {
            method: 'POST',
            // CORS preflight: panel server returns the right headers for
            // visitor.intrlock.io. We don't need the response body.
            mode: 'cors',
            headers: { 'Content-Type': 'application/json' },
            body: '{}',
            keepalive: true,
        });
    } catch {
        // Panel offline — nothing we can do from JS. Squelch.
    }
}

/** Show the on-screen keyboard. */
export const showOSK = () => panelPost('/api/keyboard/show');

/** Hide the on-screen keyboard. */
export const hideOSK = () => panelPost('/api/keyboard/hide');

/**
 * Install global focus listeners. Calls showOSK whenever a text-bearing
 * input gains focus, hideOSK on blur. Safe no-op outside kiosk mode.
 *
 * Returns a cleanup function. Idempotent — safe to call once at app boot.
 */
export function installKioskKeyboardListeners(): () => void {
    if (!isKioskMode() || typeof document === 'undefined') return () => {};

    const isTextField = (el: EventTarget | null): boolean => {
        if (!(el instanceof HTMLElement)) return false;
        if (el.isContentEditable) return true;
        const tag = el.tagName;
        if (tag === 'TEXTAREA') return true;
        if (tag !== 'INPUT') return false;
        const type = (el as HTMLInputElement).type.toLowerCase();
        // Hide non-text inputs (button, checkbox, radio, file, color, range,
        // submit, reset, etc.) — only show OSK for actual text entry.
        return [
            'text', 'tel', 'email', 'password', 'search', 'url',
            'number', 'date', 'time', 'datetime-local', 'month', 'week',
            '',  // default <input> with no type attribute is text
        ].includes(type);
    };

    const onFocusIn = (e: FocusEvent) => { if (isTextField(e.target)) showOSK(); };
    const onFocusOut = (e: FocusEvent) => { if (isTextField(e.target)) hideOSK(); };

    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('focusout', onFocusOut, true);

    return () => {
        document.removeEventListener('focusin', onFocusIn, true);
        document.removeEventListener('focusout', onFocusOut, true);
    };
}
